import { treemap, hierarchy, create, HierarchyRectangularNode } from 'd3';
import { scaleLinear } from 'd3-scale';
import { ReturnMapTreeNode } from '../model/redux-model';

const assetSubClassTitleHeight = 18;
const fontSize = 12;

export interface GenerateChartParams {
	root: ReturnMapTreeNode;
	width: number;
	height: number;
}

export const generateChart = (params: GenerateChartParams) => {
	const treemapData = hierarchy(params.root).sum(x => x.assetsUnderManagement || 0).sort((a, b) => b.value! - a.value!);
	const treemapRoot = treemap<ReturnMapTreeNode>().size([params.width, params.height])
		.padding(x => x.depth === 0 ? 5 : 0)
		.paddingTop(x => x.depth === 0 ? 0 : assetSubClassTitleHeight)
		(treemapData);

		
	const treemapSize: any = [0, 0, params.width, params.height];
	const treemapSvg = create('svg').attr('viewBox', treemapSize);

	const colorScale = scaleLinear([-0.03, 0, 0.03], ['rgb(246,53,56)', 'rgb(65,69,84)', 'rgb(48,204,90)'])
	const getColorScale = (x: number) => colorScale(Math.max(Math.min(x, 0.03), -0.03));

	const etfNodes = treemapRoot.leaves();
	const etfElements = treemapSvg
		.selectAll('etf-element')
		.data(etfNodes)
		.join('g')
		.attr('transform', x => `translate(${x.x0}, ${x.y0})`);
	etfElements
		.append('a')
		.attr('xlink:href', x => x.data.href || '#')
		.attr('target', '_blank')
		.append('rect')
		.attr('width', x => x.x1 - x.x0)
		.attr('height', x => x.y1 - x.y0)
		.style("stroke", "black")
		.style("fill", x => getColorScale(x.data.colorValue!));
	etfElements.append('text')
		.filter(x => ((x.x1 - x.x0) > params.width * 0.03) && ((x.y1 - x.y0) > params.height * 0.04))
		.attr("fill", "white")
		.attr('dominant-baseline', 'middle')
		.attr('text-anchor', 'middle')
		.style("pointer-events", "none")
		.attr('transform', x => `translate(${(x.x1 - x.x0) / 2}, ${(x.y1 - x.y0) / 2 - calculateEtfTextSize(x)})`)
		.selectAll('etf-text')
		.data(x => {
			const fontSize = calculateEtfTextSize(x);
			return [
				{ text: x.data.name, y: fontSize, fontSize: fontSize },
				{ text: x.data.colorValue === null ? '-' : (x.data.colorValue! * 100).toFixed(2) + '%', y: fontSize * 0.8, fontSize: fontSize * 0.8 }
			]
		})
		.join('tspan')
		.text(x => x.text)
		.attr('font-size', x => x.fontSize + 'px')
		.attr('x', '0')
		.attr('dy', x => x.y);

	const etfAssetSubClassNodes = treemapRoot.descendants().filter(x => x.depth === 2);
	const etfAssetSubClassElements = treemapSvg
		.selectAll('assetSubClass-element')
		.data(etfAssetSubClassNodes)
		.join('g')
		.attr('transform', x => `translate(${x.x0}, ${x.y0})`);
	etfAssetSubClassElements
		// .append('a')
		// .attr('xlink:href', x => x.data.href)
		// .attr('target', '_blank')
		.append('rect')
		.attr('width', x => x.x1 - x.x0)
		.attr('height', assetSubClassTitleHeight)
		.style('stroke', 'black')
		.style('fill', x => {
			const sumValue = x.copy().sum(x => x.colorValue!).value! / x.leaves().length;
			return getColorScale(sumValue)
		});
	etfAssetSubClassElements.append('text')
		.text(x => ellipsisText(x.data.name.toUpperCase(), x.x1 - x.x0 - 2))
		.attr('font-size', fontSize + 'px')
		.attr('font-weight', '200')
		.attr('x', 3)
		.attr('y', (assetSubClassTitleHeight - fontSize) / 2 + fontSize - 1)
		.attr('fill', 'white')
		.style("pointer-events", "none");
	etfAssetSubClassElements.append('path')
		.attr('d', 'M0 0 L8 8 L16 0')
		.attr('transform', `translate(5, ${assetSubClassTitleHeight - 1})`)
		.style('stroke', 'black')
		.style('fill', x => {
			const sumValue = x.copy().sum(x => x.colorValue!).value! / x.leaves().length;
			return getColorScale(sumValue)
		});

	const etfRegionNodes = treemapRoot.descendants().filter(x => x.depth === 1);
	const etfRegionElements = treemapSvg
		.selectAll('assetClass-element')
		.data(etfRegionNodes)
		.join('g')
		.attr('transform', x => `translate(${x.x0}, ${x.y0})`);
	etfRegionElements
		// .append('a')
		// .attr('xlink:href', x => x.data.href)
		// .attr('target', '_blank')
		.append("text")
		.text(x => ellipsisText(x.data.name.toUpperCase(), x.x1 - x.x0 - 2))
		.attr('x', 2)
		.attr('y', (assetSubClassTitleHeight - fontSize) / 2 + fontSize - 1)
		.attr("font-size", "12px")
		.attr("font-weight", "600")
		.attr("fill", "white");

	return treemapSvg.node()!;
}

function calculateEtfTextSize(node: HierarchyRectangularNode<ReturnMapTreeNode>) {
	const baseSize = Math.min(node.x1 - node.x0, node.y1 - node.y0);
	const possibleSize = baseSize * 0.15;
	const effectiveSize = Math.max(12, Math.min(50, possibleSize));
	return effectiveSize;
}

function ellipsisText(text: string, maxWidth: number) {
	var effectiveText = '';
	var effectiveWidth = 0;
	var expectedCharWidth = 8;
	var charIndex = 0;
	while (effectiveWidth <= (maxWidth - 2 * expectedCharWidth) && charIndex < text.length) {
		effectiveWidth += expectedCharWidth;
		effectiveText += text[charIndex];
		charIndex++;
	}

	return effectiveText + (charIndex === text.length ? '' : '…');
}